<script lang="ts" setup>
  import { setModelWithContext, useFormContext } from '@othercommerce/foundation';
  import { computed, watch } from 'vue';
  import type { Dictionary, CountriesDictionary, RegionsDictionary } from '@othercommerce/domain';

  interface Props {
    data: any,
    countries: CountriesDictionary,
    regions: RegionsDictionary,
    addressType: Dictionary
  }

  const props = defineProps<Props>();

  const form = useFormContext();

  // Reset region when a user changes country
  watch(() => form?.data.value.countryCode, () => {
    setModelWithContext('region', form, null);
  });

  const country = computed(() => {
    if (!form) {
      return undefined;
    }
    return form.data.value['countryCode'];
  });

  const countryRegions = computed(() => {
    if (!country.value || !props.regions) return null;

    return props.regions[country.value];
  });

</script>

<template>
  <section class="address-form">
    <div class="address-form__line address-form__type">
      <SfFormRadio :label="$t('SHOP.COMMON.LABEL.ADDRESS_TYPE')" :options="addressType" name="type" />
    </div>

    <div class="address-form__line">
      <SfFormSelect :label="$t('SHOP.COMMON.LABEL.COUNTRY')" :options="countries" name="countryCode" required />
    </div>

    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.FIRSTNAME')" name="billingFirstName" required type="text" />
    </div>

    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.LASTNAME')" name="billingLastName" required type="text" />
    </div>

    <div v-if="data.type === 'COMPANY'" class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.COMPANY')" name="billingCompany" required type="text" />
    </div>

    <div v-if="$isImplicitCountry(data.countryCode)" class="address-form__line">
      <div class="address-form__street">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.STREET')" name="street" required type="text" />
      </div>
      <div class="address-form__building-nr">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.BUILDING_NR')" name="buildingNr" required type="text" />
      </div>
      <div class="address-form__apartment-nr">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.APARTMENT_NR')" name="apartmentNr" type="text" />
      </div>
    </div>

    <div v-if="$isExplicitCountry(data.countryCode)" class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.ADDRESS_FIRST_LINE')" name="firstLine" required type="text" />
    </div>
    <div v-if="$isExplicitCountry(data.countryCode)" class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.ADDRESS_SECOND_LINE')" name="secondLine" type="text" />
    </div>

    <div class="address-form__line">
      <div class="address-form__postcode">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.POSTCODE')" name="postcode" required type="text" />
      </div>
      <div class="address-form__city">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.CITY')" name="city" required type="text" />
      </div>
    </div>

    <div class="address-form__line">
      <SfFormSelect v-if="countryRegions" :label="$t('SHOP.COMMON.LABEL.REGION')" :options="countryRegions" name="region" />

      <SfFormInput v-else :label="$t('SHOP.COMMON.LABEL.REGION')" name="region" type="text" />
    </div>

    <div v-if="data.type === 'COMPANY'" class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.VAT_ID')" name="vatId" required type="text" />
    </div>

    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.BILLING_EMAIL')" name="email" required type="email" />
    </div>
    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.BILLING_PHONE')" name="phone" required type="tel" />
    </div>
  </section>
</template>
